import { clientConfig } from '@/config/clientConfig';
import type { JobData } from '@/domains/jobs/hooks/useJobData';
import { formatDateTimeInZone } from '@/shared/lib/date-utils';
import {
  PERMIT_CATEGORY,
  PERMIT_GRANT_STATUS,
  PERMIT_GRANT_STATUS_LABEL,
  isPermitOrderType,
  permitProgressionUngated,
} from '@skewb-works/contracts';
import type {
  JobPermit,
  PermitCategory,
  PermitGrantStatus,
  PermitLifecycleStatus,
} from '@skewb-works/contracts';
import { ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * The permit workflow only applies to ZRP1 / ZRC2 order types (the card is
 * hidden otherwise). Among those, the permit category CORE resolves after the
 * SAP DISP confirmation decides what the card shows:
 *
 *  - planned / immediate → the permit reference, status and window once CORE sends a permit.
 *  - none      → no permit workflow; the card is hidden.
 *  - null / pending → category not received yet; the card shows a waiting hint.
 *
 * The Works Lite browser is view-only for permits (CWL-409, AC20). Engineers
 * Start / Extend / Stop a permit in Works Mobile (`/job-permit/*`), so this card
 * renders no action controls for any status or category.
 *
 * The card also shows the CORE-relayed outcome, reason and SAP start (CWL-419),
 * still read-only.
 *
 * A ZRP1 + immediate job with no permit row yet hides the card entirely rather
 * than showing a waiting hint — the work order is not gated on that permit
 * (CWL-125).
 */

// Human labels for the permit lifecycle (CWL-409). Record<PermitLifecycleStatus, …>
// makes tsc fail if PERMIT_STATUS gains a status without a label here.
const PERMIT_STATUS_LABEL: Record<PermitLifecycleStatus, string> = {
  arranged: 'Arranged',
  pending_start: 'Start requested',
  active: 'Notice live',
  pending_extension: 'Extension requested',
  pending_stop: 'Close requested',
  stopped: 'Closed',
};

const PERMIT_NOT_VALID = 'Permit not valid';

// CWL-419: an outcome CORE relayed that overrides the lifecycle label; null keeps the
// lifecycle label. Record<PermitGrantStatus, …> makes tsc fail if CWL-410 adds a
// status without deciding how it displays.
const GRANT_STATUS_OVERRIDE: Record<PermitGrantStatus, string | null> = {
  [PERMIT_GRANT_STATUS.PENDING_APPROVAL]:
    PERMIT_GRANT_STATUS_LABEL[PERMIT_GRANT_STATUS.PENDING_APPROVAL],
  [PERMIT_GRANT_STATUS.APPROVAL_DECLINED]:
    PERMIT_GRANT_STATUS_LABEL[PERMIT_GRANT_STATUS.APPROVAL_DECLINED],
  [PERMIT_GRANT_STATUS.REQUESTED]: null,
  [PERMIT_GRANT_STATUS.GRANTED]: null,
  [PERMIT_GRANT_STATUS.REFUSED]: PERMIT_NOT_VALID,
  [PERMIT_GRANT_STATUS.NOT_GENERATED]: PERMIT_GRANT_STATUS_LABEL[PERMIT_GRANT_STATUS.NOT_GENERATED],
  [PERMIT_GRANT_STATUS.INVALID]: PERMIT_NOT_VALID,
  [PERMIT_GRANT_STATUS.WITHDRAWN]: PERMIT_NOT_VALID,
  [PERMIT_GRANT_STATUS.REVOKE_PROPOSED]: PERMIT_NOT_VALID,
  [PERMIT_GRANT_STATUS.REVOKE_IN_PROGRESS]: PERMIT_NOT_VALID,
  [PERMIT_GRANT_STATUS.UNRECOGNISED]: null,
};

function permitStatusLabel(permit: JobPermit): string {
  const override = permit.grant_status ? GRANT_STATUS_OVERRIDE[permit.grant_status] : null;
  return override ?? PERMIT_STATUS_LABEL[permit.status];
}

function PermitCardShell({ subtitle, children }: { subtitle: string; children: ReactNode }) {
  return (
    <div data-testid="permit-card" className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-orange-50 dark:bg-orange-950 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-orange-600 dark:text-orange-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Permit</h3>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function PermitDetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="text-sm text-foreground">{value || '—'}</div>
    </div>
  );
}

// Every category that can carry a permit row has a notice-type label; a new one fails to compile.
type PermitCardCategory = Exclude<PermitCategory, 'none' | 'pending'>;
const NOTICE_TYPE_LABEL: Record<PermitCardCategory, string> = {
  [PERMIT_CATEGORY.PLANNED]: 'Planned',
  [PERMIT_CATEGORY.IMMEDIATE]: 'Immediate',
  [PERMIT_CATEGORY.IMMEDIATE_EXCEPTION]: 'Immediate Reactive',
};

export function PermitCard({ jobData }: { jobData: JobData }) {
  // Only ZRP1 / ZRC2 order types carry a permit workflow. Every other order
  // type (ZOS1, ZOS2, ZRP2, ZBF1, …) has no permit — hide the card entirely.
  if (!isPermitOrderType(jobData.orderType)) return null;

  const category: PermitCategory | null = jobData.permitCategory ?? null;

  if (category === PERMIT_CATEGORY.NONE) return null;

  // Category not received yet — the En Route gate holds the WO at Scheduled meanwhile.
  if (category == null || category === PERMIT_CATEGORY.PENDING) {
    return (
      <PermitCardShell subtitle="Awaiting permit category">
        <div className="text-sm text-muted-foreground">
          Waiting for the permit category from CORE…
        </div>
      </PermitCardShell>
    );
  }

  const permit = jobData.permit ?? null;

  if (!permit) {
    // CWL-125: a ZRP1 immediate permit may arrive late or never, and the work
    // order is not gated on it (permitProgressionUngated), so there is nothing
    // for the engineer to wait for — showing "Waiting for permit details…"
    // implied the job was blocked when it wasn't. Hide the card until a real
    // permit lands, exactly as a resolved `none` does; the card reappears the
    // moment CORE sends one.
    if (permitProgressionUngated(jobData.orderType, category)) return null;

    // A planned permit is always arranged by CORE, so it only ever waits. There
    // is no Request Permit action in the category-driven flow — CORE arranges
    // every permit off the back of the SAP DISP confirmation.
    return (
      <PermitCardShell subtitle={`${NOTICE_TYPE_LABEL[category]} permit`}>
        <div className="text-sm text-muted-foreground">
          {category === PERMIT_CATEGORY.PLANNED
            ? 'Waiting for the permit to be arranged…'
            : 'Waiting for permit details from SAP…'}
        </div>
      </PermitCardShell>
    );
  }

  return (
    <PermitCardShell subtitle={`${NOTICE_TYPE_LABEL[category]} permit`}>
      <div className="grid grid-cols-2 gap-4">
        <PermitDetailRow
          label="Permit Number"
          value={permit.permit_number ?? permit.permit_reference}
        />
        <PermitDetailRow
          label="Full Permit Reference"
          value={permit.permit_reference_number ?? ''}
        />
        <PermitDetailRow label="Notice Type" value={NOTICE_TYPE_LABEL[category]} />
        <PermitDetailRow label="Status" value={permitStatusLabel(permit)} />
        <PermitDetailRow
          label="Permit Start"
          value={formatDateTimeInZone(permit.start_date, clientConfig.features.schedulingTimeZone)}
        />
        <PermitDetailRow
          label="Permit End"
          value={formatDateTimeInZone(permit.end_date, clientConfig.features.schedulingTimeZone)}
        />
        {permit.sap_actual_start ? (
          <PermitDetailRow
            label="Started At"
            value={formatDateTimeInZone(
              permit.sap_actual_start,
              clientConfig.features.schedulingTimeZone,
            )}
          />
        ) : null}
        {permit.actual_end_date ? (
          <PermitDetailRow
            label="Ended At"
            value={formatDateTimeInZone(
              permit.actual_end_date,
              clientConfig.features.schedulingTimeZone,
            )}
          />
        ) : null}
      </div>
      {permit.status_reason ? (
        <div className="mt-4">
          <PermitDetailRow label="Reason" value={permit.status_reason} />
        </div>
      ) : null}
    </PermitCardShell>
  );
}
